import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';

const LeadershipPage = () => {
  useEffect(() => {
    document.title = 'Leadership | MACGOC Executive Team';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Meet MACGOC\'s executive leadership team with decades of combined experience across military operations, engineering, healthcare administration, finance, and international business development.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'MACGOC leadership, executive team, business consulting leaders, healthcare consulting executives, enterprise consulting leadership');
    }
  }, []);

  const executives = [
    {
      name: 'Col (R) Muhammad Anwar Chaudry',
      title: 'Chairman, Board of Directors',
      bio: 'Provides strategic oversight and governance, guiding MACGOC\'s long-term vision and institutional relationships. With decades of military leadership and strategic planning experience, Col (R) Chaudry brings disciplined operational excellence and executive authority to MACGOC\'s consulting engagements.',
      expertise: ['Strategic Leadership', 'Military Operations', 'Business Development', 'International Consulting', 'Organizational Management'],
    },
    {
      name: 'Babar A. Chaudry',
      title: 'Vice Chairman, Board of Directors | President & CEO',
      bio: 'Years of experience in civil engineering, design, commercial real estate, and large-scale housing development across Dubai and Abu Dhabi. Leads enterprise strategy, market expansion, and major development initiatives. His extensive background in large-scale infrastructure projects and real estate development provides MACGOC with deep expertise in capital-intensive business operations.',
      expertise: ['Civil Engineering', 'Commercial Real Estate', 'Housing Development', 'Enterprise Strategy', 'Market Expansion'],
    },
    {
      name: 'Taimur A. Chaudry',
      title: 'Vice President | Chief Financial Officer | Member, Board of Directors',
      bio: 'Provides financial leadership, governance, and capital strategy with exceptionally strong global experience in accounting and finance. Oversees all financial operations, capital planning, and risk management for MACGOC and its operating entities. His expertise ensures sound financial stewardship and strategic capital allocation across all consulting divisions.',
      expertise: ['Financial Leadership', 'Capital Strategy', 'Accounting & Finance', 'Risk Management', 'Corporate Governance'],
    },
    {
      name: 'Dr. Safwan A. Chaudry, MBBS',
      title: 'Deputy National Director | Chief Operating Officer | Member, Board of Directors',
      bio: 'Oversees operations across technology & digital services, healthcare operations & systems consulting, logistics & transportation advisory, gasoline retail & convenience store consulting, and business, finance & capital advisory. With his medical background and extensive healthcare administration experience, Dr. Chaudry brings unique clinical and operational expertise to all MACGOC consulting engagements.',
      expertise: ['Healthcare Operations', 'Hospital Administration', 'Clinical Governance', 'Multi-Division Operations', 'Digital Health Transformation'],
    },
  ];

  return (
    <div className="min-h-screen bg-black">
      <Navigation />
      
      <main className="pt-[90px]">
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-[#0A0A0A] to-black py-32">
          <div className="max-w-[1400px] mx-auto px-8">
            <div className="max-w-4xl">
              <div className="inline-flex items-center px-4 py-2 border border-copper/30 rounded-full mb-8">
                <span className="text-xs uppercase tracking-wider text-copper">Leadership</span>
              </div>
              <h1 className="text-6xl lg:text-7xl font-bold text-white mb-8 leading-tight">
                Guided by Proven{' '}
                <span className="bg-copper-gradient bg-clip-text text-transparent">
                  Executive Leadership
                </span>
              </h1>
              <p className="text-xl text-gray-400 leading-relaxed">
                MACGOC's strategic direction is shaped by seasoned executives with decades of combined experience across military operations, engineering, healthcare administration, finance, and international business development.
              </p>
            </div>
          </div>
        </section>

        {/* Leadership Profiles */}
        <section className="bg-charcoal-dark py-24">
          <div className="max-w-[1400px] mx-auto px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {executives.map((executive, index) => (
                <div
                  key={index}
                  className="bg-black rounded-xl p-10 border border-gray-900 hover:border-copper/30 transition-all duration-300"
                >
                  {/* Name */}
                  <h2 className="text-3xl font-bold text-white mb-2">
                    {executive.name}
                  </h2>

                  {/* Title */}
                  <p 
                    className="text-base font-medium mb-6"
                    style={{
                      background: 'linear-gradient(90deg, #A89078 0%, #C4B8A8 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    {executive.title}
                  </p>

                  {/* Divider */}
                  <div 
                    className="h-px mb-6"
                    style={{
                      background: 'linear-gradient(90deg, transparent 0%, rgba(168, 144, 120, 0.3) 50%, transparent 100%)',
                    }}
                  ></div>

                  {/* Bio */}
                  <p className="text-base text-gray-400 leading-relaxed mb-8">
                    {executive.bio}
                  </p>

                  {/* Expertise */}
                  <div>
                    <h3 className="text-sm uppercase tracking-wider text-copper-light mb-4">Areas of Expertise</h3>
                    <div className="flex flex-wrap gap-2">
                      {executive.expertise.map((area, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1.5 text-xs text-gray-400 border border-gray-800 rounded-full"
                        >
                          {area}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-black py-24 border-t border-gray-900">
          <div className="max-w-[1400px] mx-auto px-8 text-center">
            <h2 className="text-5xl font-bold text-white mb-6">
              Work With Our Leadership Team
            </h2>
            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto">
              Schedule a confidential consultation with MACGOC's executive leadership.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 text-white px-12 py-4 rounded-full text-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl"
              style={{
                background: 'linear-gradient(135deg, #7A6A5A 0%, #A89078 50%, #C4B8A8 100%)',
              }}
            >
              Request Consultation
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default LeadershipPage;
